import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LogOut, LogIn, Ticket, PoundSterling, Calendar, Trophy, Home as HomeIcon } from "lucide-react";

export default function Lotto() {
  const { user, ready, login, logout } = useAuth();
  const nav = useNavigate();

  const inMembersArea = user && (user.role === "family" || user.role === "admin");

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="lotto-scope" data-testid="lotto-scope">
      {/* Nav */}
      <header className="lotto-nav" data-testid="lotto-nav">
        <div className="lotto-container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="" className="h-10 w-10 rounded-full ring-2 ring-white/40" />
            <div>
              <div className="font-heading font-semibold leading-none">Family Syndicate</div>
              <div className="text-xs text-white/75 mt-1">Matt's Daily Savers · Private</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="hidden sm:inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg text-white/90 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="lotto-home-link"
            >
              <HomeIcon size={16} /> Main site
            </a>
            {inMembersArea ? (
              <button
                onClick={handleLogout}
                className="lotto-btn lotto-btn-accent py-2 px-3 text-sm"
                data-testid="lotto-logout"
              >
                <LogOut size={16} /> Sign out
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="lotto-container py-10">
        {!ready ? (
          <div className="text-[var(--lotto-muted)]">Loading…</div>
        ) : inMembersArea ? (
          <SyndicateDashboard user={user} />
        ) : (
          <LoginPanel doLogin={login} />
        )}
      </main>
    </div>
  );
}

function LoginPanel({ doLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await doLogin(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error || "Invalid email or password");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center pt-6">
      <div className="lg:col-span-6">
        <span className="lotto-badge lotto-badge-red mb-6">Private · Family only</span>
        <h1 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[var(--lotto-text)]">
          The <span style={{ color: "var(--lotto-primary)" }}>family</span> lottery <span style={{ color: "var(--lotto-accent)" }}>syndicate</span>.
        </h1>
        <p className="mt-5 text-[var(--lotto-text-2)] max-w-xl leading-relaxed text-lg">
          Sign in with the credentials Matt gave you to see the latest draws, running winnings and payouts.
        </p>
      </div>

      <div className="lg:col-span-6">
        <div className="lotto-card max-w-md ml-auto">
          <div className="flex items-center gap-3 mb-1">
            <span className="lotto-ball" style={{ width: 40, height: 40 }}>
              <Ticket size={18} />
            </span>
            <h2 className="font-heading font-semibold text-2xl">Members' sign in</h2>
          </div>
          <p className="text-sm text-[var(--lotto-muted)] mt-2">
            Access is restricted to invited family members.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="lotto-login-form">
            <label className="block">
              <span className="text-sm font-medium">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="lotto-input mt-2"
                data-testid="lotto-email-input"
                autoComplete="email"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="lotto-input mt-2"
                data-testid="lotto-password-input"
                autoComplete="current-password"
              />
            </label>
            {error && <p className="text-sm text-[var(--lotto-accent)]" data-testid="lotto-login-error">{error}</p>}
            <button type="submit" disabled={loading} className="lotto-btn lotto-btn-primary w-full" data-testid="lotto-login-submit">
              {loading ? "Signing in…" : (<><LogIn size={16} /> Sign in</>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function SyndicateDashboard({ user }) {
  const [content, setContent] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/lotto/content")
      .then((r) => setContent(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  if (error) return <p className="text-[var(--lotto-accent)]" data-testid="lotto-error">{error}</p>;
  if (!content) return <p className="text-[var(--lotto-muted)]">Loading…</p>;

  const isAdmin = user.role === "admin";
  const sections = content.sections || [];

  return (
    <div className="space-y-12" data-testid="lotto-dashboard">
      {/* Hero */}
      <section>
        <span className="lotto-badge">
          Signed in as {user.name || user.email}{isAdmin ? " · admin" : ""}
        </span>
        <h1 className="font-heading font-semibold text-4xl sm:text-5xl tracking-tight leading-tight mt-4">
          {content.title || "Family Syndicate"}
        </h1>
        {content.intro && (
          <p className="mt-5 text-[var(--lotto-text-2)] max-w-3xl leading-relaxed text-lg">
            {content.intro}
          </p>
        )}
      </section>

      {/* Stats */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <StatCard label="Total winnings" value={`£${(content.total_winnings || 0).toFixed(2)}`} icon={PoundSterling} />
        <StatCard label="Draws recorded" value={content.draws?.length || 0} icon={Calendar} />
        <StatCard
          label="Biggest single win"
          value={`£${((content.draws || []).reduce((m, d) => Math.max(m, d.prize || 0), 0)).toFixed(2)}`}
          icon={Trophy}
          red
        />
      </section>

      {/* Structured info sections */}
      {sections.length > 0 && (
        <section>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sections.map((s, i) => (
              <div key={i} className="lotto-card" data-testid={`lotto-section-${i}`}>
                <div className="flex items-start gap-3">
                  <span
                    className="lotto-ball flex-shrink-0"
                    style={{ background: i % 2 === 0 ? "var(--lotto-primary)" : "var(--lotto-accent)" }}
                  >
                    {i + 1}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-xl leading-tight">{s.heading || "Section"}</h3>
                    {s.body && (
                      <div className="text-[var(--lotto-text-2)] mt-3 leading-relaxed whitespace-pre-line">
                        {s.body}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Draws */}
      <section>
        <h2 className="font-heading font-semibold text-2xl sm:text-3xl mb-5">Draws so far</h2>
        {(!content.draws || content.draws.length === 0) ? (
          <div className="lotto-card">
            <p className="text-[var(--lotto-muted)]">
              No draws recorded yet. {isAdmin ? "Head to the admin panel → Lotto Page tab to add one." : "Check back after the next draw."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {content.draws.map((d, i) => (
              <DrawCard key={d.id || i} draw={d} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, red }) {
  return (
    <div className="lotto-card">
      <div className="flex items-center gap-2 text-[var(--lotto-muted)] text-xs uppercase tracking-wide">
        <Icon size={14} /> {label}
      </div>
      <div
        className="font-heading font-semibold text-3xl mt-2"
        style={{ color: red ? "var(--lotto-accent)" : "var(--lotto-primary)" }}
      >
        {value}
      </div>
    </div>
  );
}

function DrawCard({ draw }) {
  const nums = (draw.numbers || "").split(/[,\s]+/).filter(Boolean);
  return (
    <div className="lotto-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-heading font-semibold text-lg">{draw.game || "Lotto"}</div>
          <div className="text-xs text-[var(--lotto-muted)] mt-0.5">
            {draw.date ? new Date(draw.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase text-[var(--lotto-muted)] tracking-wide">Prize</div>
          <div
            className="font-heading font-semibold text-2xl"
            style={{ color: draw.prize > 0 ? "var(--lotto-accent)" : "var(--lotto-muted)" }}
          >
            £{Number(draw.prize || 0).toFixed(2)}
          </div>
        </div>
      </div>
      {nums.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {nums.map((n, i) => (
            <span key={i} className={`lotto-ball ${i >= 6 ? "lotto-ball-red" : ""}`}>{n}</span>
          ))}
        </div>
      )}
      {draw.notes && (
        <p className="text-sm text-[var(--lotto-text-2)] mt-4 leading-relaxed">{draw.notes}</p>
      )}
    </div>
  );
}
